import { ValueFormatter } from '@principle-theorem/principle-core';
import {
  IBaseChartBuilderConfig,
  ICustomReportFilterValue,
} from '@principle-theorem/principle-core/interfaces';
import { IChartConfig } from '@principle-theorem/reporting/interfaces';
import { isObject } from '@principle-theorem/shared';
import * as dc from 'dc';
import {
  compact,
  first,
  get,
  isArray,
  isFunction,
  isNull,
  isString,
  uniq,
} from 'lodash';
import { type Subject } from 'rxjs';
import { type IDCTransformResult } from '../../../../models/report/charts/data-transformers/dc-data-transformer';
import {
  applyBaseConfigToChart,
  ChartBuilderType,
  ChartBuildHelpers,
  D3_CHART_ELEM_SELECTOR,
  DEFAULT_CHART_BUILDER_CONFIG,
  type IDCChartBuilder,
  type IDCChartEvent,
} from './dc-chart-builder';

export class DcSelectMenuBuilder implements IDCChartBuilder<dc.SelectMenu> {
  readonly type = ChartBuilderType.DC;

  constructor(
    private _config: IBaseChartBuilderConfig = {
      ...DEFAULT_CHART_BUILDER_CONFIG,
      width: DEFAULT_CHART_BUILDER_CONFIG.width + 100,
    }
  ) {}

  getLabel(config: IChartConfig): string {
    return config.labels.title;
  }

  getChart(
    results: IDCTransformResult[],
    config: IChartConfig,
    eventListener$?: Subject<IDCChartEvent>
  ): dc.BaseMixin<dc.SelectMenu> | undefined {
    const result = first(results);
    const measure = first(config.builderData.measures);
    if (!result || !measure) {
      return;
    }

    const chart = this.build(D3_CHART_ELEM_SELECTOR, result, config);

    applyBaseConfigToChart(this._config, chart);

    chart.order((dataA: unknown, dataB: unknown) => {
      const valueA = this._getLabel(dataA);
      const valueB = this._getLabel(dataB);
      return valueA > valueB ? 1 : valueB > valueA ? -1 : 0;
    });

    chart.keyAccessor((data: unknown) => this._getKey(data));

    chart.title((data: unknown) => {
      const format = ValueFormatter.getFormatter(
        measure?.measure.metadata.formatter,
        measure?.measure.metadata.formatterValue
      );
      const key = this._getLabel(data);
      const value = this._getValue(data);
      return isObject(data) ? `${key} (${format(value)})` : format(value);
    });

    if (eventListener$) {
      chart.on('filtered', (eventChart, filter: unknown) => {
        const filterValues = this._resolveFilter(eventChart, filter);
        const event = ChartBuildHelpers.getDCChartEvent(
          config,
          filterValues,
          filter
        );
        eventListener$.next(event);
      });
    }

    return chart;
  }

  build(
    parent: string,
    result: IDCTransformResult,
    config: IChartConfig
  ): dc.SelectMenu {
    const chart = dc.selectMenu(parent).multiple(true).numberVisible(15);

    if (config.builderData.measures[0].filters?.length) {
      const { dimension, group } = ChartBuildHelpers.buildFilteredDimension(
        result,
        config,
        config.builderData.measures[0]
      );
      return chart.dimension(dimension).group(group);
    }

    return chart.dimension(result.dimension).group(result.group);
  }

  private _getValue(data: unknown): string {
    const value = get(data, 'value', '') as unknown;
    if (isObject(value)) {
      return String(value.valueOf());
    }
    return String(value);
  }

  private _getKey(data: unknown): string {
    const key = get(data, 'key') as unknown;
    if (isString(key)) {
      return key;
    }
    if (isObject(key) && isFunction(key.valueOf)) {
      return String(key.valueOf());
    }
    if (isObject(key)) {
      return String(get(key, 'label'));
    }
    return String(data);
  }

  private _getLabel(data: unknown): string {
    if (!isObject(data)) {
      return String(data);
    }
    return String(get(data, 'key.label', get(data, 'key', '')));
  }

  private _resolveFilter(
    chart: dc.SelectMenu,
    filter: unknown
  ): ICustomReportFilterValue[] {
    if (isNull(filter)) {
      return [];
    }
    const chartData = chart.data();
    const filterValues = isArray(filter) ? uniq(filter.flat()) : [filter];

    const mappedValues = filterValues
      .map(
        (value) =>
          chartData.find((data) => this._getKey(data) === value) as unknown
      )
      .map((data) => {
        return {
          label: this._getLabel(data),
          value: this._getKey(data),
        };
      });

    return compact(mappedValues);
  }
}
